import { Paper, Stack } from "@mui/material";
import { ComponentsCommonHeader } from "components/ComponentsCommonHeader";
import { GeneralErrorBoundary } from "components/ErrorBoundary";
import { LoadingOrEmptyWrapper } from "components/LoadingAndEmptyHandler";
import { hasInProgressJobs } from "features/dt-plus-setup/lib/processingJob";
import { LastActionTimeTracker } from "features/shared/components/LastActionTimeTracker";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import type { ProcessingJob } from "../../model/types";

import { useGetProcessingJobs } from "../../api/processingJobs";
import { EXECUTION_ALTORITHM } from "../../lib/configs";
import { ProcessingJobDetailsModal } from "./ProcessingJobDetailsModal";
import { ProcessingJobsTable } from "./ProcessingJobsTable";

export const ProcessingJobsCard = ({ className }: { className?: string }) => {
  const { t } = useTranslation();
  const { projectId } = useParams();

  const enabled = !!projectId;
  const { data, isFetching, isLoading } = useGetProcessingJobs(projectId, enabled);
  const jobs = useMemo(
    () =>
      (data ?? []).filter(
        (job) =>
          job.algorithm?.toLowerCase() === EXECUTION_ALTORITHM.RAYTRACING.name.toLowerCase() ||
          job.algorithm?.toLowerCase() === EXECUTION_ALTORITHM.PRE_RAYTRACING.name.toLowerCase()
      ),
    [data]
  );

  const [selectedJob, setSelectedJob] = useState<null | ProcessingJob>(null);

  return (
    <GeneralErrorBoundary>
      <Paper className={className}>
        <ComponentsCommonHeader
          title={
            <Stack className="gap-2">
              <span>{t("Execution.processing.title") as string}</span>
              {hasInProgressJobs(jobs) && <LastActionTimeTracker triggered={!isFetching} />}
            </Stack>
          }
        />
        <p className="mt-1 text-lds-foreground-muted font-ds-body-regular-xs">
          {t("Execution.processing.description")}
        </p>
        <LoadingOrEmptyWrapper height="20rem" showEmpty={!jobs.length} showLoading={isLoading}>
          <ProcessingJobsTable className="mt-6" items={jobs} onDetailsClick={setSelectedJob} />
        </LoadingOrEmptyWrapper>
        <ProcessingJobDetailsModal job={selectedJob} onClose={() => setSelectedJob(null)} />
      </Paper>
    </GeneralErrorBoundary>
  );
};
